import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import type {
  QuestionnaireDeliveryCode,
  QuestionnaireStatusCode,
  QuestionnaireSummaryDto,
} from '@/features/browse-questionnaires/endpoints';
import type { DeliveryStatus } from '@/features/patient-record/types';
import { queryKeys } from '@/query/keys';

const KNOWN_DELIVERY_STATUS: ReadonlySet<DeliveryStatus> = new Set<DeliveryStatus>([
  'QUEUED',
  'SENT',
  'DELIVERED',
  'BOUNCED',
  'COMPLAINED',
  'FAILED',
  'SUPPRESSED',
]);

function narrowDeliveryStatus(value: string | null | undefined): DeliveryStatus | null {
  return value !== null && value !== undefined && KNOWN_DELIVERY_STATUS.has(value as DeliveryStatus)
    ? (value as DeliveryStatus)
    : null;
}

/** `q` shorter than this is not sent (the backend ignores it anyway). */
const MIN_SEARCH_LENGTH = 2;

export interface BrowseQuestionnaireViewModel {
  readonly id: string;
  readonly personGuid: string;
  readonly patientName: string | null;
  readonly templateId: string;
  readonly templateName: string | null;
  readonly status: QuestionnaireStatusCode;
  readonly deliveryMethod: QuestionnaireDeliveryCode | null;
  readonly sentAt: string | null;
  readonly sentByName: string | null;
  /** Guid of the creating user; resolved to a display name in the view. */
  readonly createdBy: string | null;
  readonly completedAt: string | null;
  readonly daysOut: number | null;
  /** Latest delivery-attempt status, or null when the request has no delivery record. */
  readonly deliveryStatus: DeliveryStatus | null;
}

export interface BrowseQuestionnairesFilters {
  readonly status: QuestionnaireStatusCode | null;
  readonly templateId: string | null;
  /** Patient last-name substring; trimmed by the caller. */
  readonly q: string;
  /** Creating user's guid (request author); null = any creator. */
  readonly createdBy: string | null;
}

export interface BrowseQuestionnairePage {
  readonly rows: BrowseQuestionnaireViewModel[];
  readonly totalElements: number;
  readonly totalPages: number;
  readonly page: number;
}

function mapRow(dto: QuestionnaireSummaryDto): BrowseQuestionnaireViewModel {
  return {
    id: dto.id ?? '',
    personGuid: dto.person_guid ?? '',
    patientName: dto.patient_name ?? null,
    templateId: dto.template_id ?? '',
    templateName: dto.template_name ?? null,
    status: dto.status ?? 'NOT_SENT',
    deliveryMethod: dto.delivery_method ?? null,
    sentAt: dto.sent_at ?? null,
    sentByName: dto.sent_by_name ?? null,
    createdBy: dto.created_by ?? null,
    completedAt: dto.completed_at ?? null,
    daysOut: dto.days_out ?? null,
    deliveryStatus: narrowDeliveryStatus(dto.delivery_status),
  };
}

export function useBrowseQuestionnaires(
  page: number,
  size: number,
  filters: BrowseQuestionnairesFilters,
  refetchIntervalMs: number | false = false,
): UseQueryResult<BrowseQuestionnairePage> {
  const q = filters.q.trim();
  const effectiveQ = q.length >= MIN_SEARCH_LENGTH ? q : '';
  const createdBy = filters.createdBy ?? '';
  return useQuery<BrowseQuestionnairePage>({
    queryKey: queryKeys.browseQuestionnaires(
      page,
      size,
      filters.status,
      filters.templateId,
      effectiveQ,
      filters.createdBy,
    ),
    queryFn: async ({ signal }) => {
      const envelope = await apiClient.browseQuestionnaires({
        page,
        size,
        ...(filters.status !== null ? { status: filters.status } : {}),
        ...(filters.templateId !== null && filters.templateId.length > 0
          ? { templateId: filters.templateId }
          : {}),
        ...(effectiveQ.length > 0 ? { q: effectiveQ } : {}),
        ...(createdBy.length > 0 ? { createdBy } : {}),
        signal,
      });
      return {
        rows: envelope.content.map(mapRow),
        totalElements: envelope.totalElements ?? envelope.content.length,
        totalPages: envelope.totalPages ?? 1,
        page: envelope.number ?? page,
      };
    },
    refetchInterval: refetchIntervalMs,
  });
}
